import React, { useMemo } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { T, cardSx } from "utils/backrrTokens";
import AsyncTableListing from "ui-components/AsyncTableListing";

// The shared listing component, not a hand-rolled MUI table. Every other table
// in this admin is an AsyncTableListing, and a second table implementation on
// the dashboard meant a second set of header styles, sort affordances and empty
// states to keep in step with it. The rows are already in hand, so pagination
// is off — this is a short list, not a page of results.
//
// Narrow minimums on purpose: this table lives inside a card on a dashboard,
// not on a listing page with the window to itself.
const COLUMNS = [
  { Header: "Campaign", accessor: "campaign", id: "campaign", minWidth: 160 },
  { Header: "Source", accessor: "source", id: "source", minWidth: 120 },
  { Header: "Startups", accessor: "startups", id: "startups", minWidth: 90, align: "center" },
  { Header: "Investors", accessor: "investors", id: "investors", minWidth: 90, align: "center" },
];

const METRICS = [
  { key: "signups", label: "Signups" },
  { key: "startups", label: "Startups" },
  { key: "investors", label: "Investors" },
];

// Below md the same rows become cards. Five columns cannot survive a phone: a
// horizontal scroller there hides the numbers behind a gesture nobody thinks to
// make, and shrinking the columns turns the campaign name into an ellipsis. A
// card gives the name a full line and lays the three counts out beneath it,
// which is the same information read top-down instead of left-right.
const CampaignCards = ({ rows }) => (
  <Box sx={{ display: "grid", gap: 1.5 }}>
    {rows.map((row) => (
      <Box key={`${row.campaign}-${row.source}`} sx={{ ...cardSx, padding: "12px 14px" }}>
        <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: T.ink, wordBreak: "break-word" }}>
          {row.campaign}
        </Typography>
        <Typography sx={{ fontSize: "0.75rem", color: T.inkSubtle, marginTop: 0.5 }}>
          via {row.source}
        </Typography>

        <Box
          sx={{
            marginTop: 1.5,
            paddingTop: 1.5,
            borderTop: `1px solid ${T.border}`,
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 1,
          }}
        >
          {METRICS.map((m) => (
            <Box key={m.key}>
              <Typography sx={{ fontSize: "0.72rem", color: T.inkSubtle }}>{m.label}</Typography>
              <Typography
                sx={{ fontSize: "0.95rem", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
              >
                {row[m.key] ?? 0}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    ))}
  </Box>
);

// tableSx reaches AsyncTableListing's own card so a caller that already drew a
// card around this one can flatten it, instead of showing a border inside a
// border. The three dashboard variants do exactly that.
const CampaignTable = ({ rows, isLoading, tableSx }) => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));

  const data = useMemo(() => {
    return [...(rows ?? [])]
      // Biggest campaign first: the one that brought the most people is the one
      // worth reading first, and the list is short enough not to need a sort UI.
      .sort((a, b) => (b?.signups ?? 0) - (a?.signups ?? 0))
      .map((row) => ({
        ...row,
        campaign: row?.campaign || "—",
        source: row?.source || "—",
      }));
  }, [rows]);

  if (isLoading) return <Box className="skeleton" sx={{ ...cardSx, height: "14rem" }} />;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: "0.75rem", marginBottom: 1.5 }} color={T.inkSubtle}>
        Signups by acquisition campaign, and what those people became. A signup is one person; a
        startup or investor profile is what they went on to create, so the two can exceed the signups
        where one person made both.
      </Typography>

      {data.length === 0 ? (
        <Box sx={{ ...cardSx, padding: 3, textAlign: "center" }}>
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
            No campaign-attributed signups
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: T.inkSubtle, marginTop: 0.5 }}>
            Nobody arrived through a tracked campaign in this range.
          </Typography>
        </Box>
      ) : isCompact ? (
        <CampaignCards rows={data} />
      ) : (
        // Desktop keeps the shared table, with its own horizontal scroller —
        // the page's scroll container is overflow-x: hidden, so without this a
        // table wider than the card is not clipped-but-scrollable, it is gone.
        <Box sx={{ width: "100%", minWidth: 0, overflowX: "auto", height: "24rem", display: "flex", flexDirection: "column" }}>
          <AsyncTableListing
            columns={COLUMNS}
            data={data}
            pagination={false}
            count={data.length}
            // Long campaign lists scroll inside the fixed-height box above, not off the page.
            maxHeight="100%"
            noDataTitle="No campaign-attributed signups"
            noDataSubTitle="Nobody arrived through a tracked campaign in this range."
            sx={tableSx}
          />
        </Box>
      )}
    </Box>
  );
};

export default CampaignTable;
